import React, { useState, useEffect } from "react";
import { useLocation } from "react-router";
import {
  Box,
  Button,
  useTheme,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  TextField,
} from "@mui/material";
import e3logo from "../assets/e3logo.png";
import dayjs from "dayjs";
import ReplayIcon from "@mui/icons-material/Replay";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import { observer } from "mobx-react-lite";
import logStore from "../stores/LogStore";
import deviceStore from "../stores/DeviceStore";
import logAPIHandler from "../handlers/LogAPIHandler";
import warningStore from "../stores/WarningStore";
import authStore from "../stores/AuthStore";
import alertNotificationStore from "../stores/AlertNotificationStore";
import overpressureEventStore from "../stores/OverpressureEventStore";

const TopLayout = observer(() => {
  const location = useLocation();
  const isDashboard = location.pathname === "/dashboard";
  const theme = useTheme();

  const socketStatus = logStore.connectionStatus;
  const warningSocketStatus = warningStore.connectionStatus;

  const [queryForSpecificDevices, setQueryForSpecificDevices] = useState(false);
  const [selectedDevices, setSelectedDevices] = useState([]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [format, setFormat] = useState("csv");
  const [compress, setCompress] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filename, setFilename] = useState("");
  const [groupByDevice, setGroupByDevice] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");

  const [isExporting, setIsExporting] = useState(false);

  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const formatForInput = (date) => date.toISOString().slice(0, 16);

  // const [tSequential, setTSequential] = useState(null);
  // const [tParallel, setTParallel] = useState(null);
  // const fmt = (ms) => (ms == null ? "—" : `${Math.round(ms)} ms`);

  useEffect(() => {
    const user = authStore.user;
    const userID = user?.id || user?.userID;
    if (!userID) {
      console.log("No userID!");
      return;
    }
  
    const start = dayjs().subtract(1, "month").toISOString();
    const end = dayjs().toISOString();
  
    (async () => {
      try {
        await Promise.all([
          alertNotificationStore.fetchAllAlerts([], start, end, 100),
          alertNotificationStore.fetchUserReadAlertIds(userID),
        ]);
      } catch (error) {
        console.error("❌ Failed to load initial alerts (parallel):", error);
      }
    })();
  }, [authStore.user]);

  // useEffect(() => {
  //   const user = authStore.user;
  //   const userID = user?.id || user?.userID;
  //   if (!userID) return;

  //   const start = dayjs().subtract(1, "month").toISOString();
  //   const end = dayjs().toISOString();

  //   (async () => {
  //     // --- SEQUENTIAL (current approach) ---
  //     alertNotificationStore.reset(); // ensure clean slate
  //     let t0 = performance.now();
  //     try {
  //       await alertNotificationStore.fetchAllAlerts([], start, end, 100);
  //       await alertNotificationStore.fetchUserReadAlertIds(userID);
  //     } catch (e) {
  //       console.error("Sequential load failed", e);
  //     }
  //     let t1 = performance.now();
  //     setTSequential(t1 - t0);
  //     console.log(`[SEQUENTIAL] total: ${Math.round(t1 - t0)} ms`);

  //     // --- PARALLEL (proposed) ---
  //     alertNotificationStore.reset(); // clean again to be fair
  //     t0 = performance.now();
  //     try {
  //       const p1 = alertNotificationStore.fetchAllAlerts([], start, end, 100);
  //       const p2 = alertNotificationStore.fetchUserReadAlertIds(userID);
  //       await Promise.all([p1, p2]);
  //     } catch (e) {
  //       console.error("Parallel load failed", e);
  //     }
  //     t1 = performance.now();
  //     setTParallel(t1 - t0);
  //     console.log(`[PARALLEL] total: ${Math.round(t1 - t0)} ms`);
  //   })();
  // }, [authStore.user]);

  useEffect(() => {
    overpressureEventStore.loadOverpressureEventsAll();
  }, []);

  useEffect(() => {
    logStore.connect();
    warningStore.startRealtime();
    warningStore.loadWarnings();
    warningStore.loadReadWarningIds();
    return () => {
      logStore.disconnect();
    };
  }, []);

  // useEffect(() => {
  //   if (userStore.allUsers.length === 0) {
  //     userStore.fetchAllUser();
  //   }
  // }, []);

  useEffect(() => {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    setEndDate(formatForInput(now));
    setStartDate(formatForInput(oneHourAgo));
    setFilename(`logs_${formatForInput(now).replace(/[:T]/g, "-")}`);
  }, []);

  useEffect(() => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const msDiff = end - start;
    const oneDayMs = 24 * 60 * 60 * 1000;
    if (msDiff > oneDayMs) {
      setWarningMessage(
        "⚠️ The selected time range exceeds 1 day. We recommend enabling compression (.gz) to reduce file size."
      );
    } else {
      setWarningMessage("");
    }
  }, [startDate, endDate]);

  const handleDownloadClick = () => {
    const now = new Date();
    const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
    setEndDate(formatForInput(now));
    setStartDate(formatForInput(oneHourAgo));
    setFilename(`logs_${formatForInput(now).replace(/[:T]/g, "-")}`);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsExporting(false);
    setSelectedDevices([]);
    setQueryForSpecificDevices(false);
    setGroupByDevice(false);
    setIsModalOpen(false);
  };

  const handleConfirmDownload = async () => {
    if (!filename || filename.trim() === "") {
      alert("Please enter a valid filename before downloading.");
      return;
    }

    let deviceIdsToQuery = [];

    if (queryForSpecificDevices) {
      if (selectedDevices.length === 0) {
        alert("Please select at least one device.");
        return;
      }
      deviceIdsToQuery = [...selectedDevices];
    }

    try {
      setIsExporting(true);
      await logAPIHandler.exportLogs(format, {
        start: startDate || undefined,
        end: endDate || undefined,
        compress,
        filename,
        deviceIds: deviceIdsToQuery,
        groupByDevice,
      });
      handleModalClose();
    } catch (err) {
      console.error("Export failed:", err);
    } finally {
      setIsExporting(false);
    }
  };

  const getFullFilename = () => {
    let ext = format;
    if (compress && format !== "html") {
      ext += ".gz";
    }
    return `${filename || "logs_export"}.${ext}`;
  };

  return (
    <Box
      className="flex"
      sx={{
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: "background.default",
        color: "text.primary",
      }}
    >
      {/* Logo Section */}
      <Box
        className="w-64 p-4 flex items-center"
        sx={{
          borderRight: "1px solid",
          borderColor: "divider",
          bgcolor: "background.default",
        }}
      >
        <img src={e3logo} alt="E3 Company Logo" className="!w-16" />
      </Box>

      {/* Top Section Buttons */}
      <Box className="flex grow items-center justify-between p-5">
        <Chip
          variant="outlined"
          label={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                fontSize: "14px",
                minWidth: 320,
                flexWrap: "wrap",
              }}
            >
              {/* Logs WS Status */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FiberManualRecordIcon
                  sx={{
                    color:
                      socketStatus === "Connected"
                        ? "green"
                        : socketStatus === "Connecting"
                          ? "gray"
                          : "red",
                    fontSize: 14,
                  }}
                />
                <span>Logs WS: {socketStatus}</span>
                {socketStatus === "Disconnected" && (
                  <ReplayIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                      color: "red",
                      "&:hover": { color: "darkred" },
                    }}
                    onClick={() => logStore.connect()}
                  />
                )}
              </Box>

              {/* Separator */}
              <span style={{ color: theme.palette.text.disabled }}>|</span>

              {/* Warnings WS Status */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FiberManualRecordIcon
                  sx={{
                    color:
                      warningSocketStatus === "Connected"
                        ? "green"
                        : warningSocketStatus === "Connecting"
                          ? "gray"
                          : "red",
                    fontSize: 14,
                  }}
                />
                <span>Warnings WS: {warningSocketStatus}</span>
                {warningSocketStatus === "Disconnected" && (
                  <ReplayIcon
                    fontSize="small"
                    sx={{
                      cursor: "pointer",
                      color: "red",
                      "&:hover": { color: "darkred" },
                    }}
                    onClick={() => warningStore.startRealtime()}
                  />
                )}
              </Box>
            </Box>
          }
          sx={{
            fontWeight: 500,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            paddingY: 1,
            paddingX: 2,
            minHeight: 36,
          }}
        />

        {/* <Chip
          variant="outlined"
          label={
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <span>
                <strong>Load (seq):</strong> {fmt(tSequential)}
              </span>
              <span style={{ color: theme.palette.text.disabled }}>|</span>
              <span>
                <strong>Load (par):</strong> {fmt(tParallel)}
              </span>
            </Box>
          }
          sx={{
            fontWeight: 500,
            borderColor: theme.palette.divider,
            color: theme.palette.text.primary,
            px: 2,
            py: 1,
            minHeight: 36,
          }}
        /> */}

        {/* <Button
          onClick={() => {
            const userID = authStore.user?.id || authStore.user?.userID;
            if (!userID) return;
            const start = dayjs().subtract(1, "month").toISOString();
            const end = dayjs().toISOString();

            (async () => {
              // Sequential
              alertNotificationStore.reset();
              let t0 = performance.now();
              await alertNotificationStore.fetchAllAlerts([], start, end, 100);
              await alertNotificationStore.fetchUserReadAlertIds(userID);
              let t1 = performance.now();
              setTSequential(t1 - t0);

              // Parallel
              alertNotificationStore.reset();
              t0 = performance.now();
              const p1 = alertNotificationStore.fetchAllAlerts(
                [],
                start,
                end,
                100
              );
              const p2 = alertNotificationStore.fetchUserReadAlertIds(userID);
              await Promise.all([p1, p2]);
              t1 = performance.now();
              setTParallel(t1 - t0);
            })();
          }}
          sx={{ color: "text.primary" }}
        >
          Rerun Load Test
        </Button> */}

        <Box className="flex gap-x-4">
          <Button
            sx={{
              color: "text.primary",
              visibility: isDashboard ? "visible" : "hidden",
            }}
            onClick={handleDownloadClick}
          >
            Download Log
          </Button>
          <Button
            sx={{
              color: "text.primary",
              visibility: isDashboard ? "visible" : "hidden",
            }}
          >
            Import
          </Button>
          <Button
            sx={{
              color: "text.primary",
              visibility: isDashboard ? "visible" : "hidden",
            }}
          >
            Add
          </Button>
        </Box>
      </Box>

      <Box sx={{ position: "relative" }}>
        {/* Download Modal */}
        <Dialog open={isModalOpen} onClose={handleModalClose}>
          <DialogTitle>Download Logs</DialogTitle>
          <DialogContent sx={{ position: "relative" }}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                row
                value={format}
                onChange={(e) => setFormat(e.target.value)}
              >
                <FormControlLabel
                  value="json"
                  control={<Radio />}
                  label="JSON"
                />
                <FormControlLabel value="csv" control={<Radio />} label="CSV" />
                <FormControlLabel
                  value="html"
                  control={<Radio />}
                  label="HTML"
                />
              </RadioGroup>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={compress}
                  onChange={(e) => setCompress(e.target.checked)}
                />
              }
              label="Compress (.gz)"
            />

            <TextField
              label="Filename"
              fullWidth
              value={filename}
              onChange={(e) => setFilename(e.target.value)}
              margin="dense"
            />
            <Box
              sx={{ fontSize: 14, fontStyle: "italic", color: "gray", mb: 1 }}
            >
              Final filename: <strong>{getFullFilename()}</strong>
            </Box>

            {warningMessage && (
              <Box sx={{ fontSize: 14, color: "orange", mt: 1 }}>
                {warningMessage}
              </Box>
            )}

            {/* New Checkboxes for Query Scope */}
            <Box sx={{ display: "flex", flexDirection: "column", mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!queryForSpecificDevices}
                    onChange={() => setQueryForSpecificDevices(false)}
                  />
                }
                label="Query for All Devices"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={queryForSpecificDevices}
                    onChange={() => setQueryForSpecificDevices(true)}
                  />
                }
                label="Query for Specific Devices"
              />
            </Box>

            {/* Show Autocomplete ONLY if 'Specific Devices' is selected */}
            {queryForSpecificDevices && (
              <Autocomplete
                sx={{ mt: 2, mb: 2 }}
                multiple
                options={deviceStore.devices}
                getOptionLabel={(device) => device.name || device.pimios_id}
                value={deviceStore.devices.filter((d) =>
                  selectedDevices.includes(d.pimios_id)
                )}
                onChange={(e, newValue) =>
                  setSelectedDevices(newValue.map((d) => d.pimios_id))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Devices"
                    placeholder="Select devices..."
                  />
                )}
              />
            )}

            {(!queryForSpecificDevices ||
              (queryForSpecificDevices && selectedDevices.length > 1)) && (
              <Box sx={{ mt: 1 }}>
                <FormControl component="fieldset">
                  <RadioGroup
                    row
                    value={groupByDevice ? "group" : "mix"}
                    onChange={(e) =>
                      setGroupByDevice(e.target.value === "group")
                    }
                  >
                    <FormControlLabel
                      value="mix"
                      control={<Radio />}
                      label="Mix logs (ordered by time)"
                    />
                    <FormControlLabel
                      value="group"
                      control={<Radio />}
                      label="Group logs by device"
                    />
                  </RadioGroup>
                </FormControl>
              </Box>
            )}

            <TextField
              label="Start Date"
              type="datetime-local"
              fullWidth
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              margin="dense"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: formatForInput(sevenDaysAgo),
                max: endDate,
              }}
            />
            <TextField
              label="End Date"
              type="datetime-local"
              fullWidth
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              margin="dense"
              InputLabelProps={{ shrink: true }}
              inputProps={{
                min: startDate,
                max: formatForInput(now),
              }}
            />
            {isExporting && (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  bgcolor: "rgba(255,255,255,0.6)",
                  zIndex: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleModalClose}>Cancel</Button>
            <Button
              onClick={handleConfirmDownload}
              variant="contained"
              color="primary"
            >
              Download
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
});

export default TopLayout;
